import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from 'api';

export interface TaskReadyResponse {
  message: string;
  data: {
    task_id: number;
    status: string;
    created_at: string;
    updated_at: string;
    data: {
      message: {
        core_responsibilities: {
          id: number;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
          [key: string]: any;
        };
        technical_skills: {
          [key: string]: string;
        };
        soft_skills: {
          [key: string]: any;
        };
        work_culture: {
          id: number;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
          quality_options: string[];
          [key: string]: any;
        };
        personality: {
          [key: string]: string;
        };
      };
      options: Array<{
        text: string;
        value: string;
        color?: string;
      }>;
      ui_hints: {
        show_jd_preview: boolean;
        show_expectations: boolean;
        editable: boolean;
      };
    };
    task_type: string;
  };
}

const fetchTaskStatus = async (taskId: number): Promise<TaskReadyResponse> => {
  const response = await apiClient.get<TaskReadyResponse>(
    `/assessment/task-ready/`,
    {
      params: { task_id: taskId },
    }
  );
  return response.data;
};

/**
 * Hook for polling task-ready status with TanStack Query
 */
export const useTaskReady = () => {
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // TanStack Query for fetching task status
  const {
    data: taskResponse,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['task-ready', currentTaskId],
    queryFn: () => fetchTaskStatus(currentTaskId!),
    enabled: currentTaskId !== null,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always fetch fresh data
  });

  // Handle errors from TanStack Query
  useEffect(() => {
    if (queryError) {
      const errorMessage = 
        (queryError as any)?.response?.data?.message || 
        (queryError as any)?.message || 
        'Failed to check task status';
      setError(errorMessage);
    } else {
      setError(null);
    }
  }, [queryError]);

  // Backwards compatible checkTaskStatus function
  const checkTaskStatus = useCallback(async (taskId: number) => {
    setCurrentTaskId(taskId);
    setError(null);
    
    try {
      const response = await fetchTaskStatus(taskId);
      // Invalidate and refetch the query to update the cache
      queryClient.setQueryData(['task-ready', taskId], response);
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to check task status';
      setError(errorMessage);
      return null;
    }
  }, [queryClient]);

  // Polling functionality
  const startPolling = useCallback(
    (taskId: number, interval: number = 5000) => {
      setIsPolling(true);
      setCurrentTaskId(taskId);
      
      // Clear any existing polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      const poll = async () => {
        try {
          const response = await fetchTaskStatus(taskId);
          queryClient.setQueryData(['task-ready', taskId], response);
          
          if (response?.data.status === 'completed') {
            setIsPolling(false);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } catch (err: any) {
          const errorMessage =
            err.response?.data?.message || err.message || 'Failed to check task status';
          setError(errorMessage);
        }
      };
      
      // Initial poll
      poll();
      
      // Set up interval polling
      pollingIntervalRef.current = setInterval(poll, interval);
    },
    [queryClient]
  );

  // Stop polling function
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    // Backwards compatible interface
    taskResponse,
    loading,
    error,
    isPolling,
    checkTaskStatus,
    startPolling,
    
    // Additional TanStack Query utilities
    stopPolling,
    refetch,
    queryClient,
  };
};